/**
 * Carding Alerts Inbox — auto-leveller / hank deviation / fault / efficiency
 * alerts raised when a Carding production log is approved.
 *
 * Route: /production/carding-alerts
 *
 * Workflow: OPEN → ACKNOWLEDGED → RESOLVED (with notes). DISMISSED is
 * for false positives. Supervisor acknowledges within shift; maintenance
 * or QC resolves with a CAPA reference.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Badge, Button, Card, Col, Drawer, Form, Input, Row, Select, Space,
  Statistic, Switch, Table, Tag, Tooltip, Typography, message,
} from 'antd'
import {
  AlertOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  FireOutlined, ReloadOutlined, WarningOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'

import {
  ALERT_SEVERITIES, ALERT_STATUSES, ALERT_TYPE_LABEL,
  CARD_ALERT_TYPES, SEVERITY_COLOR, STATUS_COLOR, cardingApi,
  type AlertSeverity, type AlertStatus, type CardAlert, type CardAlertType,
} from '@/features/production/cardingApi'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text, Paragraph } = Typography

export default function CardingAlertsPage() {
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [statusFilter, setStatusFilter] = useState<AlertStatus | 'OPEN_OR_ACK'>('OPEN_OR_ACK')
  const [severityFilter, setSeverityFilter] = useState<AlertSeverity | undefined>()
  const [typeFilter, setTypeFilter] = useState<CardAlertType | undefined>()
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [actionRow, setActionRow] = useState<{ row: CardAlert; mode: 'ack' | 'resolve' } | null>(null)
  const [actionForm] = Form.useForm()

  const summaryQ = useQuery({
    enabled: !!plantId,
    queryKey: ['carding-alerts-summary', plantId],
    queryFn: () => cardingApi.alertSummary(plantId!),
    refetchInterval: autoRefresh ? 60_000 : false,
  })

  const listQ = useQuery({
    enabled: !!plantId,
    queryKey: ['carding-alerts', plantId, statusFilter, severityFilter, typeFilter],
    queryFn: () => cardingApi.listAlerts({
      plant_id: plantId!,
      status: statusFilter === 'OPEN_OR_ACK' ? undefined : statusFilter,
      severity: severityFilter,
      alert_type: typeFilter,
      limit: 500,
    }),
    refetchInterval: autoRefresh ? 60_000 : false,
  })

  const rows = useMemo(() => {
    const all = listQ.data ?? []
    if (statusFilter === 'OPEN_OR_ACK') {
      return all.filter((a) => a.status === 'OPEN' || a.status === 'ACKNOWLEDGED')
    }
    return all
  }, [listQ.data, statusFilter])

  const ackMut = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      cardingApi.acknowledgeAlert(id, { notes }),
    onSuccess: () => {
      message.success('Acknowledged')
      setActionRow(null)
      actionForm.resetFields()
      qc.invalidateQueries({ queryKey: ['carding-alerts'] })
      qc.invalidateQueries({ queryKey: ['carding-alerts-summary'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Ack failed'),
  })

  const resolveMut = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      cardingApi.resolveAlert(id, { notes }),
    onSuccess: () => {
      message.success('Resolved')
      setActionRow(null)
      actionForm.resetFields()
      qc.invalidateQueries({ queryKey: ['carding-alerts'] })
      qc.invalidateQueries({ queryKey: ['carding-alerts-summary'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Resolve failed'),
  })

  const summary = summaryQ.data

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle">
        <Col>
          <Title level={3} style={{ marginBottom: 0 }}>
            <AlertOutlined style={{ marginRight: 8 }} />
            Carding Alerts
          </Title>
          <Text type="secondary">
            Auto-leveller / hank deviation / fault / efficiency alerts raised
            when a Carding production log is APPROVED. Acknowledge within the
            shift; resolve with a CAPA reference.
          </Text>
        </Col>
        <Col>
          <Space size={4}>
            <Switch checked={autoRefresh} onChange={setAutoRefresh} size="small" />
            <Text type="secondary">Auto-refresh 60s</Text>
          </Space>
        </Col>
      </Row>

      {/* KPI strip */}
      <Row gutter={16} style={{ marginTop: 16, marginBottom: 16 }}>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="Open CRITICAL"
              value={summary?.open_critical ?? 0}
              valueStyle={{ color: (summary?.open_critical ?? 0) > 0 ? '#cf1322' : '#000' }}
              prefix={<FireOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="Open"
              value={summary?.by_status?.OPEN ?? 0}
              valueStyle={{ color: (summary?.by_status?.OPEN ?? 0) > 0 ? '#fa8c16' : '#000' }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="Acknowledged"
              value={summary?.by_status?.ACKNOWLEDGED ?? 0}
              valueStyle={{ color: '#1677ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="Resolved"
              value={summary?.by_status?.RESOLVED ?? 0}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="WARNING"
              value={summary?.by_severity?.WARNING ?? 0}
              valueStyle={{ color: '#d4b106' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic title="Total" value={summary?.total ?? 0} />
          </Card>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Text>Status:</Text>
          <Select
            style={{ width: 200 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'OPEN_OR_ACK', label: 'Open + Acknowledged' },
              ...ALERT_STATUSES.map((s) => ({ value: s, label: s })),
            ]}
          />
          <Text>Severity:</Text>
          <Select
            allowClear
            style={{ width: 140 }}
            value={severityFilter}
            onChange={setSeverityFilter}
            placeholder="All"
            options={ALERT_SEVERITIES.map((s) => ({ value: s, label: s }))}
          />
          <Text>Type:</Text>
          <Select
            allowClear
            style={{ width: 260 }}
            value={typeFilter}
            onChange={setTypeFilter}
            placeholder="All alert types"
            options={CARD_ALERT_TYPES.map((t) => ({
              value: t,
              label: ALERT_TYPE_LABEL[t],
            }))}
          />
          <Button icon={<ReloadOutlined />} onClick={() => {
            listQ.refetch()
            summaryQ.refetch()
          }}>Refresh</Button>
        </Space>
      </Card>

      <Table<CardAlert>
        size="small"
        rowKey="id"
        loading={listQ.isLoading}
        dataSource={rows}
        pagination={{ pageSize: 50, showSizeChanger: true }}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: 'Severity',
            dataIndex: 'severity',
            key: 'severity',
            width: 110,
            render: (s: AlertSeverity) => (
              <Tag color={SEVERITY_COLOR[s]}>
                {s === 'CRITICAL' && <FireOutlined style={{ marginRight: 4 }} />}
                {s}
              </Tag>
            ),
          },
          {
            title: 'Type',
            dataIndex: 'alert_type',
            key: 'alert_type',
            width: 200,
            render: (t: CardAlertType) => <Text>{ALERT_TYPE_LABEL[t] || t}</Text>,
          },
          {
            title: 'Message',
            dataIndex: 'message',
            key: 'message',
            ellipsis: true,
          },
          {
            title: 'Std',
            dataIndex: 'standard_value',
            key: 'standard_value',
            align: 'right',
            width: 90,
            render: (v: number | null) => v != null ? v.toFixed(2) : '-',
          },
          {
            title: 'Actual',
            dataIndex: 'actual_value',
            key: 'actual_value',
            align: 'right',
            width: 90,
            render: (v: number | null) => v != null ? v.toFixed(2) : '-',
          },
          {
            title: 'Dev %',
            dataIndex: 'deviation_pct',
            key: 'deviation_pct',
            align: 'right',
            width: 80,
            render: (v: number | null) => v != null ? (
              <Text type="danger">{v.toFixed(2)}</Text>
            ) : '-',
          },
          {
            title: 'Raised',
            dataIndex: 'raised_at',
            key: 'raised_at',
            width: 140,
            render: (v: string) => (
              <Tooltip title={dayjs(v).format('YYYY-MM-DD HH:mm:ss')}>
                {dayjs(v).fromNow()}
              </Tooltip>
            ),
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 130,
            render: (s: AlertStatus) => (
              <Tag color={STATUS_COLOR[s]}>{s}</Tag>
            ),
          },
          {
            title: 'Action',
            key: 'action',
            fixed: 'right',
            width: 180,
            render: (_, r) => (
              <Space size={4}>
                {r.status === 'OPEN' && (
                  <Button
                    size="small"
                    onClick={() => {
                      setActionRow({ row: r, mode: 'ack' })
                      actionForm.resetFields()
                    }}
                  >
                    Ack
                  </Button>
                )}
                {(r.status === 'OPEN' || r.status === 'ACKNOWLEDGED') && (
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => {
                      setActionRow({ row: r, mode: 'resolve' })
                      actionForm.resetFields()
                    }}
                  >
                    Resolve
                  </Button>
                )}
              </Space>
            ),
          },
        ]}
      />

      <Drawer
        title={actionRow?.mode === 'ack' ? 'Acknowledge Alert' : 'Resolve Alert'}
        open={!!actionRow}
        onClose={() => { setActionRow(null); actionForm.resetFields() }}
        width={480}
        destroyOnClose
      >
        {actionRow && (
          <>
            <Card size="small" style={{ marginBottom: 12 }}>
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Space>
                  <Tag color={SEVERITY_COLOR[actionRow.row.severity]}>
                    {actionRow.row.severity}
                  </Tag>
                  <Text strong>{ALERT_TYPE_LABEL[actionRow.row.alert_type] || actionRow.row.alert_type}</Text>
                </Space>
                <Paragraph style={{ marginBottom: 0 }}>
                  {actionRow.row.message}
                </Paragraph>
                {actionRow.row.standard_value != null && (
                  <Text type="secondary">
                    Std: {actionRow.row.standard_value.toFixed(2)} · Actual: {actionRow.row.actual_value?.toFixed(2) ?? '-'} · Dev: {actionRow.row.deviation_pct?.toFixed(2) ?? '-'}%
                  </Text>
                )}
              </Space>
            </Card>
            <Form
              form={actionForm}
              layout="vertical"
              onFinish={(v) => {
                if (actionRow.mode === 'ack') {
                  ackMut.mutate({ id: actionRow.row.id, notes: v.notes })
                } else {
                  resolveMut.mutate({ id: actionRow.row.id, notes: v.notes })
                }
              }}
            >
              <Form.Item
                name="notes"
                label={actionRow.mode === 'ack' ? 'Acknowledgement notes' : 'Resolution notes / CAPA reference'}
                rules={actionRow.mode === 'resolve' ? [{ required: true, message: 'Notes required to resolve' }] : []}
              >
                <Input.TextArea
                  rows={4}
                  placeholder={
                    actionRow.mode === 'ack'
                      ? 'What did you observe? Will you investigate?'
                      : 'Root cause + action taken + CAPA-NNNN reference'
                  }
                />
              </Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={ackMut.isPending || resolveMut.isPending}
                >
                  {actionRow.mode === 'ack' ? 'Acknowledge' : 'Resolve'}
                </Button>
                <Button onClick={() => { setActionRow(null); actionForm.resetFields() }}>
                  Cancel
                </Button>
              </Space>
            </Form>
          </>
        )}
      </Drawer>
    </div>
  )
}

// Side-effect — register dayjs relativeTime if not already
import relativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(relativeTime)

// Suppress unused-import warning for Badge
void Badge
